import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DragEndEvent, useDndMonitor, useDroppable } from '@dnd-kit/core';

import { getSortRulesByStepName } from '../../../../domain';
import { sortDossierOverviews } from '../../../../resources/dossier/operations';
import { DraggedItem } from '../../../../resources/pipeline/types';
import { parseQueryString } from '../../../../shared/utilities/queryString';
import {
    useDossierOverviewsFromUrlProcess,
    useFilteredDossierOverviewsByStepName,
} from '../../../../resources/pipeline/hooks';

import { useGetAgents } from '../../../../resources/agent/hooks';

import { Props, Step } from './Step';

export function DroppableStep(
    props: Readonly<Omit<Props, 'canDrop' | 'displayLoader' | 'isOver' | 'isOverOrigin' | 'value'>>,
) {
    const location = useLocation();

    const agentsQuery = useGetAgents();
    const dossiersQuery = useDossierOverviewsFromUrlProcess();
    const isLoading = agentsQuery.isPending || dossiersQuery.isPending;

    const filteredDossierOverviews = useFilteredDossierOverviewsByStepName(
        props.name,
        parseQueryString(location.search),
    );

    const sortedDossierOverviews = useMemo(() => {
        return sortDossierOverviews(filteredDossierOverviews, getSortRulesByStepName(props.name));
    }, [filteredDossierOverviews, props.name]);

    const { isOver, setNodeRef } = useDroppable({
        id: props.name,
    });
    const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
    const [canDrop, setCanDrop] = useState(false);

    useDndMonitor({
        onDragStart: (event: DragEndEvent) => {
            const draggedItem = event.active.data.current as DraggedItem;
            setDraggedItem(draggedItem);
            setCanDrop(
                draggedItem.dossierCanChangeStepTo.includes(props.name) ||
                    props.name === draggedItem.currentStep,
            );
        },
        onDragEnd: () => {
            setDraggedItem(null);
            setCanDrop(false);
        },
    });

    const isOverOrigin = draggedItem?.currentStep === props.name;

    return (
        <Step
            ref={setNodeRef}
            canDrop={canDrop}
            displayLoader={isLoading}
            isOver={isOver}
            isOverOrigin={isOverOrigin}
            value={sortedDossierOverviews}
            {...props}
        />
    );
}
